import { HttpHeaders } from '@angular/common/http';
import { DatePipe } from '@angular/common';

/**
 * Shared timezone strings for API headers (matches prior inline logic in CommonService).
 * Kept pure so tests/agents can reason about headers without the full service.
 */
export function getRequestTimezoneStrings(datePipe: DatePipe): {
  timeZone: string;
  localTimeZone: string;
} {
  const timeZone = datePipe.transform(
    new Date(),
    'MM/dd/yyyy hh:mm:ss a'
  ) as string;
  const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return { timeZone, localTimeZone };
}

/** Same rules as CommonService.BuildHeaderWithToken for raw token normalization. */
export function stripBearerPrefix(rawToken: string): string {
  return rawToken.startsWith('Bearer ')
    ? rawToken.replace(/^Bearer\s+/i, '')
    : rawToken;
}

/**
 * Authorization + timezone headers (lowercase `authorization`, matches GetAuthorizationHeader).
 */
export function buildAuthorizationTimezoneHeaders(
  bearerTokenValue: string,
  datePipe: DatePipe
): HttpHeaders {
  const { timeZone, localTimeZone } = getRequestTimezoneStrings(datePipe);
  let headers = new HttpHeaders();
  headers = headers.set('authorization', 'Bearer ' + bearerTokenValue);
  headers = headers.set('timeZone', timeZone);
  headers = headers.set('localTimeZone', localTimeZone);
  return headers;
}

/**
 * JSON + lowercase `authorization` + timezone (matches GetHeader when token present).
 */
export function buildJsonAuthHeaders(
  bearerTokenValue: string,
  datePipe: DatePipe
): HttpHeaders {
  const { timeZone, localTimeZone } = getRequestTimezoneStrings(datePipe);
  let headers = new HttpHeaders();
  headers = headers.set('Content-Type', 'application/json');
  headers = headers.set('authorization', 'Bearer ' + bearerTokenValue);
  headers = headers.set('timeZone', timeZone);
  headers = headers.set('localTimeZone', localTimeZone);
  return headers;
}

/**
 * Capital-`Authorization` JSON headers (matches BuildHeaderWithToken).
 */
export function buildJsonAuthHeadersWithCapitalAuthorization(
  tokenWithoutPrefix: string,
  datePipe: DatePipe
): HttpHeaders {
  const { timeZone, localTimeZone } = getRequestTimezoneStrings(datePipe);
  let headers = new HttpHeaders();
  headers = headers.set('Content-Type', 'application/json');
  headers = headers.set('Authorization', 'Bearer ' + tokenWithoutPrefix);
  headers = headers.set('timeZone', timeZone);
  headers = headers.set('localTimeZone', localTimeZone);
  return headers;
}

/**
 * Multipart/form header (matches CommonService.GetFormHeader): only `authorization`.
 */
export function buildFormAuthorizationHeaderOnly(
  bearerTokenValue: string
): HttpHeaders {
  let headers = new HttpHeaders();
  headers = headers.set('authorization', 'Bearer ' + bearerTokenValue);
  return headers;
}
